import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import { Table } from '@components/table/Table';
import * as styles from '@components/dataGrid/DataGrid.styles';
import { TableGridBaseColDef, TableGridRenderCellParams } from '@components/table/types/TableColumnDef.type';
import { OverflowTooltip } from '@components/tooltip/OverflowTooltip';
import { Tooltip } from '@components/tooltip/Tooltip';
import { useSearchPolicyTerms } from '@services/policyTerm/useSearchPolicyTerms';
import { useGetPolicy } from '@services/policy';
import { PolicyTerm } from '@models/policyTerm';
import { formatDateToTimezone } from '@utils/date';
import { formatCurrency } from '@utils/number';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';

const usePolicyTermsAdapter = (_params: Record<string, unknown>, _enabled: boolean) => {
  const { policyId } = useParams();
  const { policyTerms, isLoading, isFetching, isError } = useSearchPolicyTerms({
    policyId: policyId ?? '',
    queryParams: { pageLimit: 100 },
  });
  return { data: policyTerms, isLoading, isFetching: isFetching ?? false, isError };
};

const WRITTEN_PREMIUM_TOOLTIP = 'Policy Term Written Premium is unavailable';

const getPolicyTermsColumns = ({
  policyNumberPrefix,
  policyNumber,
}: {
  policyNumberPrefix: string;
  policyNumber: string;
}): TableGridBaseColDef[] => [
  {
    field: 'policyTerm',
    headerName: 'Policy Term',
    flex: 1,
    sortable: false,
    renderCell: (params: TableGridRenderCellParams<PolicyTerm>) => (
      <OverflowTooltip>
        {getFormattedPolicyNumber(policyNumberPrefix, policyNumber, params.row.termCount)}
      </OverflowTooltip>
    ),
  },
  {
    field: 'termType',
    headerName: 'Term Type',
    flex: 1,
    sortable: false,
    renderCell: (params: TableGridRenderCellParams<PolicyTerm>) => (
      <OverflowTooltip>{params.row.createdByPolicyTransaction?.transactionType ?? '-'}</OverflowTooltip>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    sortable: false,
    renderCell: (params: TableGridRenderCellParams<PolicyTerm>) => (
      <OverflowTooltip>{params.row.status}</OverflowTooltip>
    ),
  },
  {
    field: 'effective',
    headerName: 'Effective',
    flex: 1.5,
    sortable: false,
    renderCell: (params: TableGridRenderCellParams<PolicyTerm>) => (
      <OverflowTooltip>
        {`${formatDateToTimezone(params.row.effectiveOn)} - ${formatDateToTimezone(params.row.expiresOn)}`}
      </OverflowTooltip>
    ),
  },
  {
    field: 'policyTermCost',
    headerName: 'Written Premium',
    flex: 1,
    sortable: false,
    renderCell: (params: TableGridRenderCellParams<PolicyTerm>) => {
      const value = params.row.policyTermCost;
      if (value === null || value === undefined) {
        return (
          <Tooltip title={WRITTEN_PREMIUM_TOOLTIP} arrow>
            <Typography variant='bodySmall' component='span' sx={{ cursor: 'default' }}>
              -
            </Typography>
          </Tooltip>
        );
      }
      return <OverflowTooltip>{formatCurrency(value)}</OverflowTooltip>;
    },
  },
];

export const PolicyTermsTab = () => {
  const { policyId } = useParams();
  const { policy } = useGetPolicy({ policyId });

  const columns = useMemo(
    () =>
      getPolicyTermsColumns({
        policyNumberPrefix: policy?.policyNumberPrefix ?? '',
        policyNumber: policy?.policyNumber?.policyNumber ?? '',
      }),
    [policy?.policyNumberPrefix, policy?.policyNumber?.policyNumber],
  );

  return (
    <Table<{ items: PolicyTerm[]; params: Record<string, unknown> }>
      ariaLabel='policy-terms-tab-grid'
      columns={columns}
      onLoadData={usePolicyTermsAdapter}
      hidePagination
      noResultsConfig={{ noResultsMessage: 'No Policy Terms found' }}
      filterConfig={{
        filterQueryParam: null,
      }}
      sxProps={{
        tableContainer: {
          ...styles.dataGridContainer,
          display: 'block',
          height: 'fit-content',
        },
        dataGridContainer: {
          ...styles.dataGridContainer,
          borderLeft: 'none',
          padding: 0,
          height: 'auto',
        },
        dataGrid: styles.dataGrid,
      }}
    />
  );
};
